import { Box, useBreakpointValue } from '@chakra-ui/react';
import * as React from 'react';

import { MembershipRenewalModal } from '@/components/MembershipRenewalModal';
import { useElementHeight } from '@/hooks/useElementHeight';
import { useWindowHeight } from '@/hooks/useWindowHeight';
import {
  Blueprint,
  BlueprintPortal,
  useBlueprint,
} from '@/layouts/components/Blueprint';

import { HummusMobileNavigation } from './components/HummusMobileNavigation';
import { HummusNavigation } from './components/HummusNavigation';

const scrollbar = {
  '::-webkit-scrollbar': {
    display: 'none',
  },
  msOverflowStyle: 'none',
  scrollbarWidth: 'none',
};

const HummusContext = React.createContext<{ footerHeight: number }>({
  footerHeight: 0,
});

const useHummus = () => React.useContext(HummusContext);

const HummusNav: React.FC = (props) => {
  const windowHeight = useWindowHeight();
  const { headerHeight } = useBlueprint();

  return (
    <Box
      alignSelf="flex-start"
      as="nav"
      display={{ base: 'none', lg: 'block' }}
      h={`${windowHeight - headerHeight}px`}
      mx="-1"
      overflowY="auto"
      position="sticky"
      px="1"
      py="4"
      top={`${headerHeight}px`}
      w={{ base: 60, xl: 72 }}
      sx={scrollbar}
    >
      {props.children}
    </Box>
  );
};

export const HummusMain: React.FC<{ isFullWidth?: boolean, disableInternalScroll?: boolean }> = (props) => {
  return (
    <Box as="main" py="4" px="1" mx="-1" flex="1" overflowY={props.disableInternalScroll ? "visible" : "auto"}>
      {props.children}
    </Box>
  );
};

export const HummusAside: React.FC = (props) => {
  const windowHeight = useWindowHeight();
  const { headerHeight } = useBlueprint();
  const { footerHeight } = useHummus();

  const height = useBreakpointValue({
    base: windowHeight - headerHeight - footerHeight,
    lg: windowHeight - headerHeight,
  });

  return (
    <Box
      alignSelf="flex-start"
      as="aside"
      display={{ base: 'none', md: 'block' }}
      h={`${height}px`}
      mx="-1"
      overflowY="auto"
      position="sticky"
      px="1"
      py="4"
      top={`${headerHeight}px`}
      w={{ base: 72, lg: 60, xl: 72 }}
      sx={scrollbar}
    >
      {props.children}
    </Box>
  );
};

export const HummusPortal = BlueprintPortal;

export const Hummus: React.FC = (props) => {
  const footer = useElementHeight();

  return (
    <HummusContext.Provider value={{ footerHeight: footer.height }}>
      <Blueprint>
        <Box
          display="flex"
          gap={{ base: 2, md: 4 }}
          maxW="container.xl"
          mx="auto"
          px="4"
          mb={`${footer.height}px`}
        >
          <HummusNav>
            <HummusNavigation />
          </HummusNav>
          {props.children}
          <MembershipRenewalModal />
        </Box>

        <Box
          display={{ base: 'block', lg: 'none' }}
          as="nav"
          position="fixed"
          bottom="0"
          left="0"
          right="0"
          ref={footer.ref}
          zIndex="docked"
        >
          <HummusMobileNavigation />
        </Box>
      </Blueprint>
    </HummusContext.Provider>
  );
};
